import { cn } from '@/lib/utils';
import { noop } from 'lodash';
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import type { Portfolio } from 'venn-api';
import type { DropMenuItem } from 'venn-ui-kit';
import { GetColor, Icon } from 'venn-ui-kit';
import type { CompareType } from '../contexts';
import { OptimalPortfolioContext, UserContext } from '../contexts';
import { AllocationPanelContext } from './AllocationPanelContext';
import PortfolioCompareSubjectSelector from './compare-selector/PortfolioCompareSubjectSelector';
import { Header } from './Layout';
import { PercentageToggle } from './PercentageToggle';
import TradesToggle from './TradesToggle';
import { Match } from 'effect';
import { AllocationAsOfDateSelector } from './allocation-as-of-date-dropdown/AllocationAsOfDateSelector';
import { PORTFOLIO_LAB_OPTIONS_NEGATIVE_ALLOCATIONS_CASH_POSITIONS_DISABLED_MESSAGE } from 'venn-utils';

export interface AllocationHeaderProps {
  selected: CompareType;
  masterName: string | undefined;
  hideMaster: boolean;
  lastSavedPortfolio: Portfolio | undefined;
  // We won't allow user to toggle percentage mode when the total is 0
  invalidTotal: boolean;
  hasAllocationError: boolean | undefined;
  isPercentageMode: boolean;
  comparisonColor: string;
  lightComparisonColor: string;
  isTradesView: boolean;
  tradesToggleDisabled: boolean;
  toggleTradesView: () => void;
  setIsPercentageMode: (value: boolean) => void;
  onChangeComparison: (option: DropMenuItem<CompareType>, specificPortfolioVersion?: Portfolio) => void;
  onMouseEnter: ((e: React.MouseEvent<HTMLDivElement>) => void) | undefined;
  onMouseLeave: ((e: React.MouseEvent<HTMLDivElement>) => void) | undefined;
  disabledOptimizationMessage: string | undefined;
  hideComparisonColumn: boolean;
}

export const AllocationHeader = ({
  masterName,
  hideMaster,
  onChangeComparison,
  selected = 'None',
  onMouseEnter,
  onMouseLeave,
  lastSavedPortfolio,
  isPercentageMode,
  setIsPercentageMode,
  hasAllocationError,
  invalidTotal,
  comparisonColor,
  lightComparisonColor,
  tradesToggleDisabled,
  isTradesView,
  toggleTradesView,
  disabledOptimizationMessage,
  hideComparisonColumn,
}: AllocationHeaderProps) => {
  const { hasPermission } = useContext(UserContext);
  const { hasAccessToOptimization: optimizationEnabled } = useContext(OptimalPortfolioContext);
  const allocationPanelContext = useContext(AllocationPanelContext);
  const isPortfolioLabDisabled =
    allocationPanelContext.allocationPanelType === 'full' && allocationPanelContext.isPortfolioLabDisabled;

  const canOptimize = hasPermission('OPTIMIZATION');

  const allocationPanelContextValue = useContext(AllocationPanelContext);
  const updatedDisabledOptimizationMessage = isPortfolioLabDisabled
    ? PORTFOLIO_LAB_OPTIONS_NEGATIVE_ALLOCATIONS_CASH_POSITIONS_DISABLED_MESSAGE
    : disabledOptimizationMessage;

  const options = getOptions(
    hideMaster,
    optimizationEnabled,
    masterName,
    updatedDisabledOptimizationMessage,
    canOptimize,
  );
  const errorMessage = hasAllocationError
    ? 'Ensure allocations sum up to 100%'
    : invalidTotal
      ? 'Add at least one non-zero allocation'
      : undefined;

  const allocationAsOfSelector = useMemo(
    () =>
      Match.value(allocationPanelContextValue).pipe(
        Match.when({ allocationPanelType: 'historical read-only' }, (context) => (
          <AllocationAsOfDateSelector allocationPanelContextValue={context} />
        )),
        Match.when({ allocationPanelType: 'full' }, () => null),
        Match.exhaustive,
      ),
    [allocationPanelContextValue],
  );
  return (
    <Header onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <HeaderRow>
        <Main hideComparisonColumn={hideComparisonColumn}>
          <Name>Name</Name>
          <Navs>
            {Match.value(allocationPanelContextValue.allocationPanelType).pipe(
              Match.when('full', () => <span>Current</span>),
              Match.when('historical read-only', () => (
                <>
                  <span>Allocations as of:</span>
                  {allocationAsOfSelector}
                </>
              )),
              Match.exhaustive,
            )}
            <PercentageToggle
              disabled={!!errorMessage}
              tooltipMessage={errorMessage}
              toggled={isPercentageMode}
              setToggled={setIsPercentageMode}
              className={cn(!hideComparisonColumn && 'mb-[1.5px]')}
            />
          </Navs>
        </Main>
        {!hideComparisonColumn && (
          <Comparison color={comparisonColor}>
            <PortfolioCompareSubjectSelector
              lastSavedPortfolio={lastSavedPortfolio}
              comparisonColor={comparisonColor}
              options={options}
              selectedValue={selected}
              // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
              onChange={onChangeComparison ?? noop}
            />
          </Comparison>
        )}
      </HeaderRow>

      <HeaderRow>
        <EmptySpace />
        {!hideComparisonColumn && (
          <Comparison color={lightComparisonColor} dense>
            <Trades>Trades</Trades>
            <TradesToggle disabled={tradesToggleDisabled} toggled={isTradesView} onToggle={toggleTradesView} />
          </Comparison>
        )}
      </HeaderRow>
    </Header>
  );
};

const getOptions = (
  hideMaster: boolean,
  optimizationEnabled: boolean,
  masterName: string | undefined,
  disabledOptimizationMessage: string | undefined,
  canOptimize: boolean,
): DropMenuItem[] => {
  const options: DropMenuItem[] = [
    {
      label: 'None',
      value: 'None',
      className: 'qa-option-none',
    },
  ];
  if (!hideMaster) {
    options.push({
      label: masterName ? `Master (${masterName})` : 'Master',
      value: 'Master',
      className: 'qa-option-master',
    });
  }
  if (optimizationEnabled && canOptimize) {
    const disabledProps = disabledOptimizationMessage
      ? {
          disabled: true,
          tooltipContent: disabledOptimizationMessage,
        }
      : {};
    const portfolioLabProps = {
      label: 'Optimized with Portfolio Lab',
      icon: <FlaskIcon type="flask" />,
      description: 'Metrics driven by your portfolio policy, historical data, and long-term forecasts.',
    };
    options.push({
      value: 'Optimized',
      className: 'qa-option-optimized',
      ...disabledProps,
      ...portfolioLabProps,
    });
  }
  return options;
};

const HeaderRow = styled.div`
  display: flex;
  width: 100%;
  color: ${GetColor.White};
  font-size: 12px;
`;

const EmptySpace = styled.div`
  flex-grow: 1;
  background-color: ${GetColor.GreyScale.Grey10};
`;

const Trades = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: ${GetColor.Black};
  margin-right: 4px;
`;

const Main = styled.div<{ hideComparisonColumn: boolean }>`
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${({ hideComparisonColumn }) => (hideComparisonColumn ? 32 : 35)}px;
  border-bottom: 1px solid ${GetColor.GreyScale.Grey30};
  color: ${GetColor.Black};
  font-weight: bold;
`;

const Name = styled.div`
  padding-left: 8px;
`;

const Navs = styled.div`
  text-align: end;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 4px;
`;

const Comparison = styled.div<{ color: string; dense?: boolean }>`
  width: 105px;
  background-color: ${({ color }) => color};
  height: ${({ dense }) => (dense ? 27 : 35)}px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FlaskIcon = styled(Icon)`
  margin-right: 4px;
`;
